// The owner's nominee roster, on a page of its own.
//
// Nominees used to be the tenth tile in the vault's category grid, edited through the
// same ManageCategory modal as a will or a bank account. They were never the same kind
// of thing: a vault record is the owner's own information, while a nominee is a THIRD
// PARTY's contact details, held under a declaration the owner signed, and the operational
// list the dead-man's switch acts on. Migration 009 gave them their own table; this gives
// them their own screen, so the consent that makes holding them lawful is stated where it
// applies rather than tucked into a record form.
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Mail, Pencil, Send, ShieldOff, Trash2, UserPlus, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Button from '../components/elements/Button';
import Input from '../components/elements/Input';
import Info from '../components/elements/Info';
import Modal from '../components/Modal';
import ConfirmModal from '../components/ConfirmModal';
import Notification from '../components/Notification';
import { getNominees, addNominee, updateNominee, deleteNominee } from '../utils/nomineesApi';
import { isValidEmail, toDateTimeLocal } from '../utils/globalUtils';

const EMPTY_FORM = {
  email: '',
  name: '',
  relationship: '',
  contact: '',
  notes: '',
  nomineeConsentAccepted: false,
  nomineeAccessInfoAccepted: false,
};

const NomineesPage = () => {
  const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
  const role = sessionStorage.getItem('role');
  const navigate = useNavigate();

  const [nominees, setNominees] = useState([]);
  const [maxNominees, setMaxNominees] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: 'success' });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getNominees();
      if (res.success) {
        setNominees(res.nominees || []);
        setMaxNominees(res.maxNominees);
      } else {
        setNotification({ message: res.message || 'Could not load your nominees.', type: 'error' });
      }
    } catch {
      setNotification({ message: 'Could not load your nominees.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Nominees | Eternal Locker™';
    if (!isLoggedIn) {
      navigate('/');
      return;
    }
    // A nominee reading an owner's vault must never land here - the roster is the other
    // nominees' personal data. The server refuses them (every /nominees route is
    // owner-only), so this is about not offering a page that would only 403.
    if (role === 'nominee') {
      navigate('/dashboard');
      return;
    }
    load();
  }, [isLoggedIn, role, navigate, load]);

  // maxNominees is null for PLATINUM, meaning unlimited - which is why this compares
  // against null explicitly rather than testing truthiness. A cap of 0 (FREE) is a real
  // cap, and `!maxNominees` would read it as "no limit", offering an Add button to
  // exactly the users who cannot use it.
  // Someone who objected to being designated is still ON this list - the owner has to be
  // able to see that they declined - but they are not a nominee any more: the switch skips
  // them, and the server does not count them against the plan cap either. Counting them
  // here would charge the owner a slot for somebody else's decision.
  const activeNominees = nominees.filter((nominee) => !nominee.objectedAt);
  const atCap = maxNominees !== null && activeNominees.length >= maxNominees;

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError({});
    setFormOpen(true);
  };

  const openEdit = (nominee) => {
    setEditing(nominee);
    setForm({
      email: nominee.email || '',
      name: nominee.name || '',
      relationship: nominee.relationship || '',
      contact: nominee.contact || '',
      notes: nominee.notes || '',
      // THE EDIT FORM SHOWS WHAT THE RECORD HOLDS; it does not re-collect consent. The
      // declaration starts ticked because the owner accepted it for this nomination and the
      // server preserves the original `consentAt` - correcting a phone number is not a fresh
      // act of consent, so what is on screen is a fact being shown back rather than a tick
      // being assumed. (An earlier version of this comment claimed re-confirming was still
      // required to submit. It is not: a pre-ticked box asks nothing, and saying otherwise
      // described an intention rather than the code.)
      //
      // The access-information box is different on purpose: it reflects the stored value, so
      // a nomination saved while that acknowledgement was optional renders UNTICKED and has
      // to be answered before the edit will save (EL-30).
      //
      // `NOMINEE_CONSENT_FORMAT.md` covers why "unchecked by default" governs the add form
      // rather than this one, and the one nuance left standing: an edit rewrites
      // `consentVersion`, so a notice that changed since the nomination would be recorded as
      // accepted without a fresh read - reachable only if the re-consent gate at login did
      // not intercept the change first.
      nomineeConsentAccepted: true,
      nomineeAccessInfoAccepted: Boolean(nominee.accessInfoAcknowledged),
    });
    setFormError({});
    setFormOpen(true);
  };

  const validate = () => {
    const errors = {};
    if (!form.email || !isValidEmail(form.email)) errors.email = 'A valid email address is required';
    if (!form.name || !form.name.trim()) errors.name = 'Nominee name is required';
    if (!form.relationship || !form.relationship.trim()) errors.relationship = 'Relationship is required';
    if (!form.nomineeConsentAccepted) errors.nomineeConsentAccepted = 'You must accept the nominee consent declaration to continue.';
    // The second box is about the owner's OWN data, not the nominee's: the Nominee Access
    // Information page is where what the switch discloses, to whom and after how long is
    // set out. Designating a nominee without having been shown that is agreeing to the
    // disclosure of a whole vault sight unseen, which is why it is a condition of saving
    // and not a suggestion (EL-30). The server enforces it too - this is the courtesy.
    if (!form.nomineeAccessInfoAccepted) errors.nomineeAccessInfoAccepted = 'You must confirm that you have read the Nominee Access Information.';

    // Duplicate check mirrors the server's 409, which is the one that actually holds
    // (a UNIQUE index, not a check-then-write). This just gives a better message than a
    // round-trip would.
    // Objected rows are deliberately excluded from this check. Re-adding that address is
    // also refused, but for a different reason and with a different remedy - the server
    // answers 409 NOMINEE_OBJECTED and says so in words the owner can act on. Catching it
    // here would replace that with "already on your list", which is both untrue and
    // unhelpful: the fix is not to find the existing entry, it is to ask someone else.
    const duplicate = activeNominees.some((nominee) =>
      nominee.email.toLowerCase() === form.email.trim().toLowerCase()
      && (!editing || nominee.id !== editing.id));
    if (duplicate) errors.email = 'This nominee is already on your list';

    setFormError(errors);
    return Object.keys(errors).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        email: form.email.trim(),
        name: form.name.trim(),
        relationship: form.relationship.trim(),
        contact: form.contact.trim(),
        notes: form.notes.trim(),
        nomineeConsentAccepted: form.nomineeConsentAccepted,
        nomineeAccessInfoAccepted: form.nomineeAccessInfoAccepted,
      };
      const res = editing
        ? await updateNominee(editing.id, payload)
        : await addNominee(payload);

      if (res.success) {
        setFormOpen(false);
        setNotification({ message: editing ? 'Nominee updated.' : 'Nominee added.', type: 'success' });
        await load();
      } else {
        // The server owns the cap, the duplicate rule and the consent requirement; show
        // what it said rather than guessing which of them was hit.
        setNotification({ message: res.message || 'Could not save this nominee.', type: 'error' });
      }
    } catch {
      setNotification({ message: 'Could not save this nominee.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const target = confirmDelete;
    setConfirmDelete(null);
    if (!target) return;
    try {
      const res = await deleteNominee(target.id);
      if (res.success) {
        setNotification({ message: 'Nominee removed.', type: 'success' });
        await load();
      } else {
        setNotification({ message: res.message || 'Could not remove this nominee.', type: 'error' });
      }
    } catch {
      setNotification({ message: 'Could not remove this nominee.', type: 'error' });
    }
  };

  if (!isLoggedIn || role === 'nominee') return null;

  return (
    <Layout>
      <Notification
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: '', type: 'success' })}
      />

      <div className="flex sm:flex-row flex-col justify-between items-center border-b border-accent-main pb-4">
        <h1 className="sm:text-3xl text-2xl font-extrabold text-accent-main flex items-center">
          <Users className="mr-3 w-7 h-7" />
          Your Nominees
        </h1>
        <div className="ml-auto flex items-center gap-2">
          <Button size="small" onClick={openAdd} disabled={atCap || loading}>
            <UserPlus className="w-4 h-4 mr-1 inline" /> Add Nominee
          </Button>
        </div>
      </div>

      <div className="py-4 space-y-3">
        <p className="text-lg text-primary-dark m-0">
          These are the people who will be given access to your locker if you are inactive
          long enough for the inactivity check to complete.
        </p>
        {/* Stated up front rather than discovered from a card badge. Owners are being told
            here that we contact these people on their behalf - which is not something to
            find out about after the fact, and is the reason an objection can arrive. */}
        <p className="text-sm text-primary-dark m-0">
          We email each person you add to tell them you have named them, what we hold about
          them and why. They can decline, and if they do we will not contact them about your
          locker again.
        </p>
        {maxNominees !== null && (
          <p className="text-sm text-primary-dark m-0">
            Your plan allows {maxNominees} {maxNominees === 1 ? 'nominee' : 'nominees'}.
            You have {activeNominees.length}.
          </p>
        )}
        {atCap && (
          <Info type="warning">
            You have reached your plan&apos;s nominee limit. Upgrade your plan to add more.
          </Info>
        )}
      </div>

      {loading ? (
        <p className="text-primary-dark">Loading your nominees…</p>
      ) : nominees.length === 0 ? (
        <div className="p-6 bg-primary-light border border-accent-main rounded-xl text-center space-y-3">
          <AlertTriangle className="w-8 h-8 text-accent-main mx-auto" />
          <h3 className="text-xl font-bold text-primary-dark">No nominees yet</h3>
          <p className="text-primary-dark text-sm">
            Without a nominee, nobody can be granted access to your locker. Add at least one
            person so your records reach someone.
          </p>
          <Button size="small" onClick={openAdd} disabled={atCap}>Add your first nominee</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {nominees.map((nominee) => (
            <div key={nominee.id} className="p-6 bg-primary-light border border-accent-main rounded-xl space-y-2 shadow-lg">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-xl font-bold text-primary-dark break-words">{nominee.name || nominee.email}</h3>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* An objected nomination is read-only: the owner can remove it, and that
                      is all. Editing would restart a relationship this person ended - not
                      least by letting their address be moved onto a different record. The
                      server refuses it too (409 NOMINEE_OBJECTED); this is about not
                      offering a control that only errors. */}
                  {!nominee.objectedAt && (
                    <button
                      type="button"
                      aria-label={`Edit ${nominee.email}`}
                      onClick={() => openEdit(nominee)}
                      className="p-1 text-accent-main hover:text-accent-dark"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    aria-label={`Remove ${nominee.email}`}
                    onClick={() => setConfirmDelete(nominee)}
                    className="p-1 text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-primary-dark text-sm flex items-center break-all">
                <Mail className="w-4 h-4 mr-2 flex-shrink-0" />{nominee.email}
              </p>
              {nominee.relationship && (
                <p className="text-primary-dark text-sm">Relationship: {nominee.relationship}</p>
              )}
              {nominee.contact && (
                <p className="text-primary-dark text-sm">Contact: {nominee.contact}</p>
              )}
              {nominee.notes && (
                <p className="text-primary-dark text-sm italic">{nominee.notes}</p>
              )}
              {/* WHAT THIS PERSON WAS TOLD, AND WHAT THEY SAID. Three states, kept
                  distinct: they declined; they have been notified; or the notice did not
                  go out, which now means only one thing - the send failed. Collapsing the
                  last two would let an owner believe someone was told when they were not,
                  which is the exact assumption finding F4 is about, and would also hide
                  the one case they can act on by re-saving the nominee. */}
              {nominee.objectedAt ? (
                <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg p-3 space-y-1">
                  <p className="font-semibold flex items-center m-0">
                    <ShieldOff className="w-4 h-4 mr-2 flex-shrink-0" />Declined this nomination
                  </p>
                  <p className="m-0">
                    They asked us not to hold their details, on {toDateTimeLocal(nominee.objectedAt)}.
                    They will not be contacted or given access. Designate someone else, and
                    it is worth speaking to them first.
                  </p>
                </div>
              ) : nominee.noticeSentAt ? (
                <p className="text-xs text-text-muted flex items-center">
                  <Send className="w-3 h-3 mr-2 flex-shrink-0" />
                  Notified {toDateTimeLocal(nominee.noticeSentAt)}
                </p>
              ) : (
                <p className="text-xs text-amber-800 flex items-center">
                  <AlertTriangle className="w-3 h-3 mr-2 flex-shrink-0" />
                  Not notified yet — we could not reach this address
                </p>
              )}
              {/* The consent record, shown rather than hidden: the owner should be able to
                  see what they agreed to and when, without asking anyone. */}
              {nominee.consentAt && (
                <p className="text-xs text-text-muted pt-2 border-t border-accent-main/30">
                  Consent recorded {toDateTimeLocal(nominee.consentAt)}
                  {nominee.consentVersion ? ` (notice version ${nominee.consentVersion})` : ''}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        icon={<UserPlus />}
        title={editing ? 'Edit Nominee' : 'Add Nominee'}
      >
        <div className="space-y-3">
          <Input
            label="Email"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Enter nominee email address"
            error={formError.email}
            info="This is the address the one-time access password is sent to."
          />
          <Input
            label="Nominee Name"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Rohan Patel, Anjali Singh, Suresh Kumar"
            error={formError.name}
          />
          <Input
            label="Relationship"
            required
            value={form.relationship}
            onChange={(e) => setForm({ ...form, relationship: e.target.value })}
            placeholder="e.g. Son, Daughter, Spouse, Nephew"
            error={formError.relationship}
          />
          <Input
            label="Contact Number"
            value={form.contact}
            onChange={(e) => setForm({ ...form, contact: e.target.value })}
            placeholder="e.g. +91-9876543210, 022-12345678"
          />
          <Input
            label="Notes"
            type="textarea"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="e.g. Share after my passing, nominee for all accounts"
          />

          <div className="rounded-lg border border-accent-main bg-primary-light/50 p-3 space-y-3">
            <p className="text-sm text-primary-dark">
              <strong>Nominee Consent Declaration (Schedule 14 format):</strong> I confirm that
              this nominee has been informed about this nomination and I authorize Eternal
              Locker™ to store nominee details and enable the nominee access workflow as
              described in the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-accent-main underline hover:text-accent-dark">Terms</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-accent-main underline hover:text-accent-dark">Privacy Policy</a>.
            </p>
            <label className="flex items-start gap-2 text-sm text-primary-dark cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 accent-accent-main"
                checked={Boolean(form.nomineeConsentAccepted)}
                onChange={(e) => setForm({ ...form, nomineeConsentAccepted: e.target.checked })}
              />
              I confirm and accept the nominee consent declaration.
            </label>
            {formError.nomineeConsentAccepted && (
              <p className="text-sm text-red-600">{formError.nomineeConsentAccepted}</p>
            )}
            <label className="flex items-start gap-2 text-sm text-primary-dark cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 accent-accent-main"
                checked={Boolean(form.nomineeAccessInfoAccepted)}
                onChange={(e) => setForm({ ...form, nomineeAccessInfoAccepted: e.target.checked })}
              />
              I have read and understood the{' '}
              <a href="/nominee-access-info" target="_blank" rel="noopener noreferrer" className="text-accent-main underline hover:text-accent-dark">Nominee Access Information</a>
            </label>
            {formError.nomineeAccessInfoAccepted && (
              <p className="text-sm text-red-600">{formError.nomineeAccessInfoAccepted}</p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="secondary" onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} loading={saving}>{editing ? 'Update' : 'Add'}</Button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={Boolean(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={remove}
        title="Remove this nominee?"
        message={confirmDelete
          ? `${confirmDelete.name || confirmDelete.email} will no longer be able to access your locker, and will not be notified if the inactivity check completes.`
          : ''}
        confirmText="Remove"
        cancelText="Cancel"
      />
    </Layout>
  );
};

export default NomineesPage;
